import { useCallback, useMemo, useState } from "react";
import { useTellescopeInfiniteQuery } from "../use-tellescope-infinite-query";
import { useTellescopeMutation } from "../use-tellescope-mutation";
import { useAuth } from "../../auth";

export interface UseAISummaryArgs {
  /**
   * A unique id/hash that represents the current context from which the summary will be generated
   * Maximum of 100 characters
   *
   * @example - `timeline-summary-${enduserId}-${hash}`
   */
  type: string;
  /**
   * The prompt that describes the context that will be provided from which to generate a summary, as well as how the
   * summary should be formatted
   *
   * @example
   * ```
   * You are a clinical documentation assistant. When given a list of patient timeline events, respond with a single
   * plain-text paragraph summarizing the patient's recent activity. Do not use headers, bullet points, markdown,
   * labels, or any formatting. Write only the summary paragraph and nothing else.
   * ```
   */
  prompt: string;
  enabled?: boolean;
  onOutOfCredits?: () => void;
}

export interface GenerateSummaryArgs {
  /**
   * The message that contains the context needed to generate the summary according to the provided prompt
   *
   * @example - `Patient: ${enduserName}\n\nTimeline:\n${formattedContext}`
   */
  message: string;
  /**
   * Optional prompt override for this single generation. When omitted, the hook's configured `prompt`
   * is used. Useful when the caller wants to vary the prompt per generation (e.g. adding a focus)
   * without changing the summary's lookup `type`.
   */
  prompt?: string;
}

export interface UseAISummaryReturn {
  summary?: string;
  /** Timestamp of the existing summary's assistant message, when one exists. */
  summaryDate?: Date;
  canGenerateNewSummary: boolean;
  isOutOfCredits: boolean;
  generateSummary: ({ message, prompt }: GenerateSummaryArgs) => Promise<string | undefined>;
  isGenerating: boolean;
  isLoading: boolean;
}

export const useAISummary = ({
  type,
  prompt,
  enabled = true,
  onOutOfCredits,
}: UseAISummaryArgs): UseAISummaryReturn => {
  const { selectedOrganization } = useAuth();
  const existingSummaryQuery = useTellescopeInfiniteQuery({
    model: "ai_conversations",
    method: "getSome",
    request: {
      filter: {
        type,
      },
      limit: 1,
    },
    enabled: !!type && enabled,
    select: (data) => {
      const assistantMessage = data.pages
        .at(0)
        ?.at(0)
        ?.messages.find((message) => message.role === "assistant");
      if (!assistantMessage) return undefined;
      return { text: assistantMessage.text, timestamp: assistantMessage.timestamp };
    },
    placeholderData: (prev) => prev, // Keep previous summary
  });
  const [hasReceivedOutOfCreditsError, setHasReceivedOutOfCreditsError] = useState(false);

  const isOutOfCredits = useMemo(
    () => hasReceivedOutOfCreditsError || (selectedOrganization?.creditCount ?? Infinity) <= 0,
    [hasReceivedOutOfCreditsError, selectedOrganization?.creditCount]
  );
  const canGenerateNewSummary = useMemo(
    () => !isOutOfCredits && !!existingSummaryQuery.isFetched,
    [existingSummaryQuery.isFetched, isOutOfCredits]
  );

  const generateSummaryMutation = useTellescopeMutation({
    model: "ai_conversations",
    method: "send_message",
    onError: (error) => {
      if (error.message.startsWith("Organization has") && error.message.endsWith("credits left")) {
        onOutOfCredits?.();
        setHasReceivedOutOfCreditsError(true);
      }
    },
  });

  const generateSummary = useCallback<UseAISummaryReturn["generateSummary"]>(
    async ({ message, prompt: promptOverride }) => {
      const promptToUse = promptOverride ?? prompt;
      if (!canGenerateNewSummary || !enabled || !promptToUse) return;

      const res = await generateSummaryMutation.mutateAsync({
        message,
        prompt: promptToUse,
        type,
      });

      return res.ai_conversation.messages.find((message) => message.role === "assistant")?.text;
    },
    [canGenerateNewSummary, enabled, generateSummaryMutation, prompt, type]
  );

  return {
    summary: existingSummaryQuery.data?.text,
    summaryDate: existingSummaryQuery.data?.timestamp,
    canGenerateNewSummary,
    isOutOfCredits,
    generateSummary,
    isGenerating: generateSummaryMutation.isPending,
    isLoading: existingSummaryQuery.isLoading,
  };
};
